import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import useProperty from '../../property/hooks/useProperty';
import useNotificationCount from '../hooks/useNotificationCount';
import AirbnbSyncErrorWidget from './AirbnbSyncErrorWidget';
import BookingActionsWidget from './BookingActionsWidget';
import CleaningWidget from './CleaningWidget';

const NotificationButton = () => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { propertyId, loading: propertyLoading } = useProperty();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: notificationData, loading: notificationLoading } =
        useNotificationCount({
            params: [propertyId ?? 0],
            skip: !propertyId,
            pollInterval: 10_000,
        });

    const totalCount = notificationData?.totalCount ?? 0;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    if (propertyLoading || notificationLoading) {
        return (
            <Box
                component="button"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    px: 2,
                    border: '2px solid #374151',
                    borderRadius: 0,
                    backgroundColor: 'rgba(55, 65, 81, 0.2)',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#6b7280',
                }}
            >
                ...
            </Box>
        );
    }

    const hasNotifications = totalCount > 0;

    if (!hasNotifications) {
        return null;
    }

    return (
        <>
            <Box
                component="button"
                onClick={handleClick}
                aria-label={`${totalCount} Actions`}
                data-testid="notification-button"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    px: 2,
                    border: '2px solid',
                    borderRadius: 0,
                    borderColor: hasNotifications ? '#f59e0b' : '#374151',
                    backgroundColor: hasNotifications
                        ? 'rgba(245, 158, 11, 0.1)'
                        : 'transparent',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: hasNotifications ? '#f59e0b' : '#9ca3af',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: hasNotifications ? '#fbbf24' : '#6366f1',
                        backgroundColor: hasNotifications
                            ? 'rgba(245, 158, 11, 0.2)'
                            : 'rgba(99, 102, 241, 0.1)',
                        color: hasNotifications ? '#fbbf24' : '#6366f1',
                    },
                }}
            >
                {isSmallScreen ? totalCount : `${totalCount} Actions`}
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 0,
                            border: '3px solid #f59e0b',
                            backgroundColor: '#1a1a2e',
                            minWidth: 350,
                            maxWidth: 450,
                            maxHeight: '70vh',
                            overflow: 'auto',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        borderBottom: '2px solid #f59e0b',
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: '#f59e0b',
                        }}
                    >
                        Actions Required ({totalCount})
                    </Typography>
                </Box>
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <AirbnbSyncErrorWidget />
                    <CleaningWidget />
                    <BookingActionsWidget />
                </Box>
            </Popover>
        </>
    );
};

export default NotificationButton;
